import { useEffect, useRef } from "react";
import { getOrCreateSingleton } from "./singleton";

export type GlobalPointerState = {
	x: number;
	y: number;
	clientX: number;
	clientY: number;
	isActive: boolean;
};

const INITIAL_POINTER_STATE: GlobalPointerState = {
	x: 0,
	y: 0,
	clientX: 0,
	clientY: 0,
	isActive: false,
};

// All mutable module-scope state is anchored on globalThis so that HMR re-evaluation
// or Vite serving this file under multiple module IDs cannot fork the listener set,
// the pointer state, or the rAF/viewport bookkeeping. Without this, two instances of
// this module would each install their own pointermove listeners, double-emit to
// different subscriber sets, and quietly desync.
type PointerInternals = {
	pointerState: GlobalPointerState;
	subscribers: Set<() => void>;
	isListening: boolean;
	viewportWidth: number;
	viewportHeight: number;
	pendingClientX: number | null;
	pendingClientY: number | null;
	pointerRafId: number | null;
};

const internals = getOrCreateSingleton<PointerInternals>("globalPointer.internals", () => ({
	pointerState: INITIAL_POINTER_STATE,
	subscribers: new Set<() => void>(),
	isListening: false,
	viewportWidth: 1,
	viewportHeight: 1,
	pendingClientX: null,
	pendingClientY: null,
	pointerRafId: null,
}));

const emit = () => {
	for (const callback of internals.subscribers) callback();
};

const emitIfChanged = (nextState: GlobalPointerState) => {
	const ps = internals.pointerState;
	if (
		ps.x === nextState.x &&
		ps.y === nextState.y &&
		ps.clientX === nextState.clientX &&
		ps.clientY === nextState.clientY &&
		ps.isActive === nextState.isActive
	) {
		return;
	}
	internals.pointerState = nextState;
	emit();
};

const updateViewportSize = () => {
	internals.viewportWidth = Math.max(window.innerWidth, 1);
	internals.viewportHeight = Math.max(window.innerHeight, 1);
};

const flushPointerMove = () => {
	internals.pointerRafId = null;
	if (internals.pendingClientX === null || internals.pendingClientY === null) return;

	const x = (internals.pendingClientX / internals.viewportWidth) * 2 - 1;
	const y = -((internals.pendingClientY / internals.viewportHeight) * 2 - 1);
	emitIfChanged({
		x,
		y,
		clientX: internals.pendingClientX,
		clientY: internals.pendingClientY,
		isActive: true,
	});
};

const handlePointerMove = (event: PointerEvent) => {
	internals.pendingClientX = event.clientX;
	internals.pendingClientY = event.clientY;
	if (internals.pointerRafId !== null) return;
	internals.pointerRafId = window.requestAnimationFrame(flushPointerMove);
};

const deactivatePointer = () => {
	internals.pendingClientX = null;
	internals.pendingClientY = null;
	if (internals.pointerRafId !== null) {
		window.cancelAnimationFrame(internals.pointerRafId);
		internals.pointerRafId = null;
	}
	if (!internals.pointerState.isActive) return;
	emitIfChanged({ ...internals.pointerState, isActive: false });
};

const handleVisibilityChange = () => {
	if (document.visibilityState !== "visible") deactivatePointer();
};

const handlePointerOut = (event: PointerEvent) => {
	if (event.relatedTarget === null) deactivatePointer();
};

const ensureListeners = () => {
	if (internals.isListening || typeof window === "undefined") return;
	internals.isListening = true;
	updateViewportSize();
	window.addEventListener("pointermove", handlePointerMove, { passive: true });
	document.addEventListener("pointerout", handlePointerOut, { passive: true });
	window.addEventListener("blur", deactivatePointer);
	window.addEventListener("resize", updateViewportSize, { passive: true });
	document.addEventListener("visibilitychange", handleVisibilityChange);
};

const teardownListeners = () => {
	if (!internals.isListening || typeof window === "undefined" || internals.subscribers.size > 0) return;
	internals.isListening = false;
	if (internals.pointerRafId !== null) {
		window.cancelAnimationFrame(internals.pointerRafId);
		internals.pointerRafId = null;
	}
	window.removeEventListener("pointermove", handlePointerMove);
	document.removeEventListener("pointerout", handlePointerOut);
	window.removeEventListener("blur", deactivatePointer);
	window.removeEventListener("resize", updateViewportSize);
	document.removeEventListener("visibilitychange", handleVisibilityChange);
};

const subscribe = (callback: () => void) => {
	internals.subscribers.add(callback);
	ensureListeners();
	return () => {
		internals.subscribers.delete(callback);
		teardownListeners();
	};
};

const getSnapshot = () => internals.pointerState;

export function useGlobalPointerRef() {
	const pointerRef = useRef<GlobalPointerState>(getSnapshot());

	useEffect(() => {
		const unsubscribe = subscribe(() => {
			pointerRef.current = getSnapshot();
		});
		return unsubscribe;
	}, []);

	return pointerRef;
}
